import { homerunnerApiBaseUrl } from '../hr'
import { okOrThrow } from './http'

/**
 * The ONE `['property', filter]` query, and the only place its options are built.
 *
 * dashboard#1517 pointed `usePropertyCustomFields` at the key the page's
 * calendar/booking widget already uses (`useFetchProperty`), which is the right
 * dedup — but each hook kept its OWN `queryFn`. react-query stores `queryFn`
 * and `retry` on the *query*, applied from whichever observer INITIATES the
 * fetch, so on a cache miss the first consumer to subscribe decided how the
 * page's property record was fetched. On a CSR property-details-layout page the
 * custom-fields block renders before the slot children that hold
 * booking/calendar, so the weaker fetcher won and silently dropped three guards
 * that exist for the money path:
 *
 *   - a 200 carrying `{status:'error'}` or a null result must ERROR the query.
 *     Consumers read success-with-no-result as "still loading", so BookingForm's
 *     occupancy guard never settles and quoting stays disabled with nothing on
 *     screen (BookingForm.tsx, common/occupancy.ts).
 *   - a 15s bound, so a hung request cannot hold quoting unsettled forever.
 *   - `retry: 1`, because the client default (3 exponential retries) turns a
 *     dead endpoint into ~a minute of disabled quoting before the guard can
 *     settle permissive.
 *
 * Every consumer of this key now builds from `propertyQueryOptions()` and adds
 * only a `select`, the same shape `usePropertyDetails` uses to let
 * property-title/-meta/-map share one fetch (common/property-details.ts). Do
 * not introduce a second `queryFn` for `['property', …]` — which fetcher runs
 * would become a function of widget mount order again.
 */

/** Structural shape shared by CalendarFilter and PropertyCustomFieldsFilter. */
export type PropertyQueryFilter = {
  feed_id: number
  platform?: string | null
  property: string
}

/** `result` is non-null by construction: the queryFn throws on an error envelope. */
export type PropertyEnvelope<T> = {
  status: 'ok' | 'error'
  result: T
}

// A property fetch that never resolves never errors, and the booking occupancy
// guard would hold quoting unsettled (disabled) forever — bound it. Composed by
// hand with AbortController rather than AbortSignal.timeout/any: on platforms
// that have timeout but not any (Chrome 103-115, Safari 16-17.3), the pair
// would silently drop react-query's own cancellation signal.
const PROPERTY_FETCH_TIMEOUT_MS = 15000
export function boundedFetchSignal(outer?: AbortSignal): AbortSignal | undefined {
  if (typeof AbortController === 'undefined') return outer
  const controller = new AbortController()
  const timer = setTimeout(() => {
    controller.abort(typeof DOMException !== 'undefined'
      ? new DOMException('Property fetch timed out', 'TimeoutError')
      : new Error('Property fetch timed out'))
  }, PROPERTY_FETCH_TIMEOUT_MS)
  controller.signal.addEventListener('abort', () => clearTimeout(timer), { once: true })
  if (outer) {
    if (outer.aborted) controller.abort(outer.reason)
    else outer.addEventListener('abort', () => controller.abort(outer.reason), { once: true })
  }
  return controller.signal
}

/**
 * The shared key. `platform` rides in the key (it distinguishes cache entries)
 * but NOT in the query string: `GET /public-api/v1/property/{slug}?feed_id={id}`
 * is the documented contract (docs/widget-system.md), and it is what
 * `useFetchProperty` and `widgets/calendar/ssr.ts` have always sent.
 */
export function propertyQueryKey(filter: PropertyQueryFilter) {
  return ['property', filter] as const
}

export async function fetchProperty<T>(filter: PropertyQueryFilter, signal?: AbortSignal): Promise<PropertyEnvelope<T>> {
  const url = new URL(`${homerunnerApiBaseUrl()}/public-api/v1/property/${encodeURIComponent(filter.property)}`)
  url.searchParams.set('feed_id', String(filter.feed_id))
  url.searchParams.set('property', filter.property)
  const res = await fetch(url.toString(), { signal: boundedFetchSignal(signal) })
  await okOrThrow(res, 'Failed to fetch property')
  const data = await res.json() as PropertyEnvelope<T | null>
  // A 200 carrying an error envelope must ERROR the query, not succeed:
  // consumers read success-with-no-result as "still loading", and the booking
  // occupancy guard would wait on it forever instead of taking its
  // settled-permissive error path.
  if (data.status !== 'ok' || !data.result) throw new Error('Failed to fetch property')
  return data as PropertyEnvelope<T>
}

/**
 * Options for the shared property query. Spread it and add only `select`
 * (and `enabled`) — never a second `queryFn`.
 */
export function propertyQueryOptions<T>(filter: PropertyQueryFilter) {
  return {
    queryKey: propertyQueryKey(filter),
    queryFn: ({ signal }: { signal?: AbortSignal }) => fetchProperty<T>(filter, signal),
    // Bounded on purpose: the client default (3 exponential retries) turns a
    // dead endpoint into ~a minute of unsettled — i.e. disabled — quoting
    // before the occupancy guard can settle permissive.
    retry: 1,
    retryDelay: 1000,
  }
}
